import {QueryOptions} from '@apollo/client';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FileOpenParams} from 'file-browser/FileBrowserActions';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BufferState, BufferStates} from 'Navigation';
import {asJQueryPromise} from 'util/Promises';

import {
  ProcedureFileStateQuery,
  ProcedureFileStateQueryVariables,
} from './graphql/__generated/ProcedureFileStateQuery';
import {PROCEDURE_FILE_STATE_QUERY} from './graphql/ProceduresQueries';
import ProcedureTabState from './ProcedureTabState';

type Procedure = ProcedureFileStateQuery['procedure'];

interface ProcedureFileStateAttributes {
  procedure?: Procedure;
  versionApiId?: string;
  editUrl?: string;
  name?: string;
  hideTabNav?: boolean;
  archiveRecord?: ArchiveRecord | null;
}

class ProcedureFileState extends ApolloFileState<
  ProcedureFileStateQuery,
  ProcedureFileStateQueryVariables,
  ProcedureFileStateAttributes
> {
  static fileType = ModelTypeConstants.PROCEDURE;
  static getQueryOptions(fileId: string): QueryOptions<ProcedureFileStateQueryVariables> {
    return {
      query: PROCEDURE_FILE_STATE_QUERY,
      variables: {
        procedureId: fileId,
      },
    };
  }

  static dataToAttributes(data: ProcedureFileStateQuery): ProcedureFileStateAttributes {
    const {procedure} = data;
    return isInaccessible(procedure)
      ? {}
      : {
          procedure,
          editUrl: procedure.editUrl,
          name: procedure.name,
          archiveRecord: procedure.archiveRecord,
        };
  }

  static openFile(fileId: string, queryKey?: FileOpenParams): JQueryPromise<ProcedureFileState> {
    return asJQueryPromise(getApolloClient().query(this.getQueryOptions(fileId))).then(
      (response) =>
        new this({
          id: fileId,
          versionApiId: queryKey?.versionApiId,
          ...(response != null ? this.dataToAttributes(response.data) : {}),
        })
    );
  }

  procedureTab!: ProcedureTabState;

  defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.SingleBufferStatusBarState(),
      hideTabNav: BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE,
    };
  }
  initialize(...args: Array<unknown>): void {
    super.initialize(...args);

    this.procedureTab = new ProcedureTabState({
      procedureId: this.id,
      hasUnsavedChanges: false,
      versionApiId: this.get('versionApiId'),
    });

    const buffers = new BufferStates([new BufferState()]);
    this.set({buffers});
    this.buffers().at(0).tabs().add(this.procedureTab);

    this.buffers().at(0).set({active: true});
    this.setIsWorkspaceSplit(false);
  }

  editURL(): string | undefined {
    return this.get('editUrl');
  }

  name(): string {
    return this.get('name') ?? 'Loading...';
  }

  archiveRecord(): ArchiveRecord | null {
    return this.get('archiveRecord') ?? null;
  }

  setArchiveRecord(archiveRecord: ArchiveRecord | null): void {
    this.set('archiveRecord', archiveRecord);
  }

  // This is present so that when a procedure is archived due to its project being archived, an error isn't
  // thrown due to setPermissions being absent. However, we don't need to do anything here because the
  // Procedure view will poll if opened and fetch the updated permissions.
  setPermissions(): void {}

  registerTitleAndURLSync(): void {
    this.on('change:name change:url', () => {
      this.syncTitleAndURL();
    });
  }

  hasUnsavedChanges(): boolean {
    return this.procedureTab.get('hasUnsavedChanges');
  }
}

export default ProcedureFileState;
